<template>
  <div class="chart-wrapper">
    <div class="chart-header">
      <h3 class="chart-title">Exames realizados por Faixa Etária e Gênero</h3>
    </div>
    <BiChartEmpty v-if="!loading && !hasData" />
    <template v-else>
      <div class="chart-filter">
        <select
          v-model="selectedExam"
          class="chart-select"
          @change="onFilterChange"
        >
          <option value="ALL">Exames: Todos</option>
          <option
            v-for="examOption in examOptions"
            :key="examOption.code"
            :value="examOption.code"
          >
            {{ examOption.description }}
          </option>
        </select>
      </div>

      <div
        v-if="loading"
        class="age-range-pyramid__skeleton"
        :style="{ height: `${chartHeight}px` }"
      >
        <div
          v-for="range in ranges"
          :key="range"
          class="age-range-pyramid__skeleton-row"
        >
          <span />
          <span />
        </div>
      </div>
      <v-chart
        v-else
        class="chart"
        :style="{ height: `${chartHeight}px` }"
        :option="option"
        autoresize
        @click="onBarClick"
      />
    </template>
  </div>
</template>

<script>
import VChart from 'vue-echarts'
import BiChartEmpty from './BiChartEmpty.vue'
import { hasChartData } from './chartData'

const BAR_HEIGHT = 40
const BAR_GAP = 4
const GRID_VERTICAL_SPACING = 56

export default {
  name: 'AgeRangePyramidChart',
  components: { VChart, BiChartEmpty },
  props: {
    items: { type: Array, default: () => [] },
    examOptions: { type: Array, default: () => [] },
    exam: { type: String, default: 'ALL' },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedExam: this.exam,
      ranges: ['0-17', '18-29', '30-44', '45-59', '60+'],
    }
  },
  computed: {
    hasData() {
      return hasChartData(this.items, 'male') || hasChartData(this.items, 'female')
    },
    plotHeight() {
      return this.ranges.length * (BAR_HEIGHT + BAR_GAP)
    },
    chartHeight() {
      return this.plotHeight + GRID_VERTICAL_SPACING
    },
    axisInterval() {
      const largestValue = Math.max(...this.activeData.male, ...this.activeData.female, 1)
      const rawInterval = (largestValue * 1.15) / 4
      const magnitude = 10 ** Math.floor(Math.log10(rawInterval))
      const normalized = rawInterval / magnitude
      const niceNormalized = normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10
      return niceNormalized * magnitude
    },
    axisLimit() {
      const largestValue = Math.max(...this.activeData.male, ...this.activeData.female, 1)
      return Math.ceil((largestValue * 1.15) / this.axisInterval) * this.axisInterval
    },
    activeData() {
      const byRange = this.items.reduce((acc, item) => {
        acc[item.ageRange] = item
        return acc
      }, {})
      return {
        male: this.ranges.map(range => byRange[range]?.male ?? 0),
        female: this.ranges.map(range => byRange[range]?.female ?? 0),
      }
    },
    option() {
      const male = this.activeData.male
      const female = this.activeData.female
      return {
        graphic: [
          {
            type: 'text',
            left: '1.5%',
            top: 6,
            style: {
              text: 'Anos',
              fill: '#333',
              fontSize: 12,
            },
            z: 100,
          },
          {
            type: 'text',
            left: '25%',
            top: 6,
            style: {
              text: 'Masculino',
              fill: '#333',
              fontSize: 12,
              textAlign: 'center',
            },
            z: 100,
          },
          {
            type: 'text',
            left: '75%',
            top: 6,
            style: {
              text: 'Feminino',
              fill: '#333',
              fontSize: 12,
              textAlign: 'center',
            },
            z: 100,
          },
        ],
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: params => {
            const range = params[0]?.axisValue
            const m = params.find(p => p.seriesName === 'Masculino')
            const f = params.find(p => p.seriesName === 'Feminino')
            const maleCount = m ? Math.abs(m.value).toLocaleString('pt-BR') : '-'
            const femaleCount = f ? f.value.toLocaleString('pt-BR') : '-'
            return `<b>${range} anos</b><br/>
              <span style="color:#769deb">●</span> Masculino: <b>${maleCount}</b> exames<br/>
              <span style="color:#8dd0da">●</span> Feminino: <b>${femaleCount}</b> exames`
          },
        },
        legend: { show: false },
        grid: {
          top: 32,
          left: 48,
          right: 16,
          bottom: 0,
          height: this.plotHeight,
          containLabel: false,
        },
        xAxis: {
          type: 'value',
          min: -this.axisLimit,
          max: this.axisLimit,
          interval: this.axisInterval,
          axisLabel: {
            formatter: val =>
              val === 0
                ? '0'
                : `${Math.abs(val) >= 1000 ? `${(Math.abs(val) / 1000).toLocaleString('pt-BR')} K` : Math.abs(val)}`,
            fontSize: 13,
            color: '#5a5a5a',
          },
          axisLine: { lineStyle: { color: '#ccc' } },
          splitLine: { lineStyle: { color: '#f0f0f0' } },
        },
        yAxis: {
          type: 'category',
          data: this.ranges,
          axisLabel: { fontSize: 12, color: '#5a5a5a' },
          axisLine: { onZero: false, lineStyle: { color: '#ccc' } },
          axisTick: { show: false },
        },
        series: [
          {
            name: 'Masculino',
            type: 'bar',
            barWidth: BAR_HEIGHT,
            barCategoryGap: BAR_GAP,
            data: male.map(v => -v),
            itemStyle: { color: '#769deb', borderRadius: [2, 0, 0, 2] },
            label: {
              show: true,
              position: 'left',
              formatter: p => Math.abs(p.value).toLocaleString('pt-BR'),
              fontSize: 13,
              fontFamily: 'Roboto, sans-serif',
              color: '#333',
            },
          },
          {
            name: 'Feminino',
            type: 'bar',
            barWidth: BAR_HEIGHT,
            barCategoryGap: BAR_GAP,

            barGap: '-100%',
            data: female,
            itemStyle: { color: '#8dd0da', borderRadius: [0, 2, 2, 0] },
            label: {
              show: true,
              position: 'right',
              formatter: p => Math.abs(p.value).toLocaleString('pt-BR'),
              fontSize: 13,
              fontFamily: 'Roboto, sans-serif',
              color: '#161616',
            },
          },
        ],
      }
    },
  },
  watch: {
    exam(val) {
      this.selectedExam = val
    },
  },
  methods: {
    onBarClick(params) {
      this.$emit('bar-click', {
        range: params.name,
        gender: params.seriesName,
        value: Math.abs(params.value),
      })
    },
    onFilterChange() {
      this.$emit('exam-change', this.selectedExam)
    },
  },
}
</script>

<style scoped lang="scss">
.chart-wrapper {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  font-family: 'Roboto', sans-serif;
}
.chart-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.chart-title {
  font-size: 16px;
  font-weight: 700;
  color: rgba(70, 70, 70, 1);
  margin: 0;
}
.chart-filter {
  margin-bottom: 12px;
}
.chart-select {
  width: 100%;
  height: 44px;
  padding: 0 44px 0 12px;
  border: 1px solid #d5d5d5;
  border-radius: 8px;
  font-family: 'Exo 2', sans-serif;
  font-size: 13.33px;
  font-weight: 500;
  color: rgba(153, 153, 153, 1);
  background: #fff
    url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.33031 11.399L11.2159 14.2752C11.6504 14.7083 12.3523 14.7083 12.7868 14.2752L15.6724 11.399C16.3743 10.6993 15.873 9.5 14.8814 9.5H9.1102C8.11863 9.5 7.62841 10.6993 8.33031 11.399Z' fill='%237F1A54'/%3E%3C/svg%3E")
    no-repeat right 8px center / 28px 28px;
  cursor: pointer;
  appearance: none;

  &:hover,
  &:focus {
    border-color: #7f1a54;
    outline: none;
  }
}
.chart {
  width: 100%;
}
.age-range-pyramid__skeleton {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  padding: 32px 12px 0 48px;
  box-sizing: border-box;
}
.age-range-pyramid__skeleton-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;

  span {
    width: 42%;
    height: 28px;
    border-radius: 2px;
    background: #e9e9e9;
    animation: age-range-shimmer 1.4s ease-in-out infinite;
  }
}
@keyframes age-range-shimmer {
  0%,
  100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.9;
  }
}
</style>
